import ArrSVG from '@public/icons/btn-arr.svg';
import { useEffect } from 'react';
import { RecoilRoot } from 'recoil';
import { useSetOpenedPopupsState } from '@/shared/atoms/opened-popups';
import {
    usePavilionsValue,
    useSetErrorState,
    useSetLoadingState,
    useSetPavilionsState,
} from '@/shared/atoms/pavilionsData';
import InhouseMapPopup, { INHOUSE_MAP_NAME } from '@/shared/components/configurator/InhouseMapPopup';
import StreetExpositionPopup, { STREET_EXPOSITION_NAME } from '@/shared/components/configurator/StreetExpositionPopup';
import Image from '@/shared/components/shared/Image';
import { ImageShape, Theme } from '@/shared/lib/types';
import { tp } from '@/shared/lib/typograf';
import { PAGES } from '../../../../consts';
import { pavilionsData } from '../../configurator/InhouseMap/inhouse-data';

export type ConstructorSectionProps = {
    h1: string;
};

type Props = React.HTMLAttributes<HTMLElement> & {
    constructorSectionProps: ConstructorSectionProps;
    theme?: Theme;
};

type ColumnProps = {
    image: ImageShape;
    title: string;
    description: string;
    onclick: () => void;
    priority?: boolean;
};

const Column = ({ image, title, description, onclick, priority }: ColumnProps) => {
    return (
        <button className="index-hero-block" onClick={onclick}>
            <Image
                src={image.src}
                alt={image.alt || ''}
                width={image.width || 1296}
                height={image.height || 2080}
                title={image.title}
                sizes="40vw"
                className="index-hero-img responsive__item"
                priority={priority}
            />
            <div className="index-hero-block__inner">
                <div className="index-hero-block__inner-text__wrapper">
                    <span className="index-hero-block__title h2" dangerouslySetInnerHTML={{ __html: title }} />
                    <span className="index-hero-block__description text-m">{description}</span>
                </div>
                <div className="index-hero-block__btn btn btn-outline-light btn-geometry-outline-light">
                    <ArrSVG className="btn__arrow" />

                    <span className="btn__text"> Перейти</span>
                </div>
            </div>
        </button>
    );
};

function ConstructorSection({ constructorSectionProps, theme, children, ...rest }: Props) {
    const h1 = tp(constructorSectionProps.h1);
    const { openPopup } = useSetOpenedPopupsState();

    const setPavilions = useSetPavilionsState();
    const setLoading = useSetLoadingState();
    const setError = useSetErrorState();

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            const result = await pavilionsData({ schemes: [PAGES.PAVILION] });
            setPavilions(result);
        };

        fetchData();
    }, [setPavilions, setLoading, setError]);
    const { pavilionData, pavilionDataLoading, pavilionDataError } = usePavilionsValue();

    return (
        <section
            className="constructor-section js-header-theme-trigger js-bg-trigger"
            data-bg="#deeffa"
            id="constructor"
            data-theme={theme}
        >
            <div className="constructor-wrapper wrapper">
                <div className="index-hero__left">
                    <h2 className="index-hero__title h3" dangerouslySetInnerHTML={{ __html: tp(h1) }} />
                    {children}
                </div>

                <div className="index-hero__right">
                    <Column
                        image={{ src: '/img/hero-street3.jpg', width: 1920, height: 1080 }}
                        title="Уличная <br> экспозиция"
                        description="от 20 м2"
                        priority
                        onclick={() => {
                            openPopup(STREET_EXPOSITION_NAME);
                        }}
                    />
                    <Column
                        image={{ src: '/img/hero-inhouse2.jpg', width: 1920, height: 1080 }}
                        title="Павильон"
                        description="от 9 м2"
                        onclick={() => {
                            openPopup(INHOUSE_MAP_NAME);
                        }}
                    />
                </div>
                {Object.keys(pavilionData.data).length > 0 && (
                    <RecoilRoot>
                        <InhouseMapPopup />
                        <StreetExpositionPopup />
                    </RecoilRoot>
                )}
            </div>
        </section>
    );
}

export default ConstructorSection;
